import React, { useCallback, useContext, useState } from 'react';
import { Button, Grid, TextField } from '@mui/material';
import { DropzoneArea } from 'mui-file-dropzone';
import ClaimsService from 'services/api/ClaimsService';
import { ParametersContext } from 'components/ParametersGuard';
import Modal from 'components/shared/Modal';
import { useSnackbar } from 'notistack';
import { ModalProps } from 'store/types/ComponentProps';
import { Claim } from 'store/types/Claim';
import ClaimUploadType from 'store/enums/ClaimUploadType';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';

import styles from '../ClaimsPage.module.scss';

interface UploadClaimFilesModalProps extends ModalProps {
  companyId: string;
  data?: Claim;
  uploadType: ClaimUploadType;
  onSubmit: () => void;
}

const MAX_FILE_SIZE = 5000000;

const isBlobFile = (x: unknown): x is File => typeof File !== 'undefined' && x instanceof File;

const UploadClaimFilesModal: React.FunctionComponent<UploadClaimFilesModalProps> = ({
  companyId,
  data,
  uploadType,
  open,
  onClose,
  onSubmit,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    claims: { fileUploadHeader },
  } = useContext(ParametersContext);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const handleFormSubmit = useCallback(() => {
    if (data) {
      setLoading(true);

      const uploadPromiseArray: Promise<string>[] = [];
      if (files.length > 0) {
        files.forEach((file: File) => {
          if (isBlobFile(file)) {
            uploadPromiseArray.push(
              ClaimsService.uploadFile(data.id, companyId, data.type, uploadType, description, file)
            );
          }
        });
      }

      Promise.all(uploadPromiseArray)
        .then(() => {
          enqueueSnackbar('Uploaded Documents', { variant: 'success' });
          setFiles([]);
          setDescription('');
          onSubmit();
        })
        .catch((errorMessage: string) => {
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        })
        .finally(() => setLoading(false));
    }
  }, [companyId, data, description, enqueueSnackbar, files, onSubmit, uploadType]);

  const handleClose = useCallback(() => {
    setFiles([]);
    setDescription('');
    onClose();
  }, [onClose]);

  return (
    <Modal
      title={uploadType === ClaimUploadType.ProofOfPayment ? 'Upload Proof Of Payment' : 'Upload Documents'}
      open={open}
      onClose={handleClose}
      onSubmit={handleFormSubmit}
      disableBackdropClick={false}
      maxWidth={'sm'}
      loading={loading}
      actions={
        <>
          <Button color={'secondary'} variant={'outlined'} onClick={handleClose} disabled={loading}>
            {'Cancel'}
          </Button>
          <Button type={'submit'} color={'primary'} variant={'contained'} disabled={loading || files.length <= 0}>
            {'Submit'}
          </Button>
        </>
      }
    >
      <Grid {...defaultGridContainerProps}>
        {fileUploadHeader && (
          <Grid {...defaultGridItemProps}>
            <p dangerouslySetInnerHTML={{ __html: fileUploadHeader }} />
          </Grid>
        )}
        <Grid {...defaultGridItemProps}>
          <TextField
            label={'Description'}
            value={description}
            multiline
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          ></TextField>
        </Grid>
        <Grid {...defaultGridItemProps}>
          <DropzoneArea
            maxFileSize={MAX_FILE_SIZE}
            filesLimit={uploadType === ClaimUploadType.ProofOfPayment ? 1 : 5}
            fileObjects={files ?? []}
            onChange={(newFiles) => setFiles(newFiles)}
            showAlerts={['error']}
            clearOnUnmount={true}
            showPreviews
            showPreviewsInDropzone={false}
            useChipsForPreview
            previewText=""
            classes={{
              root: styles.dropzone,
              textContainer: styles.dropzoneTextContainer,
              text: styles.dropzoneText,
              icon: styles.dropzoneIcon,
            }}
            previewGridClasses={{
              container: styles.previewContainer,
            }}
            dropzoneProps={{ noDrag: false }}
            previewChipProps={{ className: styles.chip }}
            alertSnackbarProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            }}
          />
        </Grid>
      </Grid>
    </Modal>
  );
};
export default UploadClaimFilesModal;
